import {
  validateFirstName,
  validateLastName,
  validateEmail,
  validatePassword,
  validateCompany,
  validatePasswordStrength,
} from 'core/utils/validator'
import { useTranslation } from 'react-i18next'

type Field =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'password'
  | 'company'
  | 'name'
  | 'position'
  | 'code'

export const useValidation = () => {
  const { t } = useTranslation()

  // Validate and return translated error message
  const validate = (field: Field, value: string): string | null => {
    let errorKey: string | null = null

    switch (field) {
      case 'firstName':
      case 'name': // For compatibility with XState name field
        errorKey = validateFirstName(value)
        break
      case 'lastName':
        errorKey = validateLastName(value)
        break
      case 'email':
        errorKey = validateEmail(value)
        break
      case 'password':
        errorKey = validatePassword(value)
        break
      case 'company':
        errorKey = validateCompany(value)
        break
      case 'position':
        // Position validation - minimum 2 characters
        errorKey = value.trim().length < 2 ? 'enterValidPosition' : null
        break
      case 'code':
        // Verification code validation - 6 digits
        errorKey =
          value.length !== 6 || !/^\d{6}$/.test(value) ? 'enterValidCode' : null
        break
      default:
        return null
    }

    // Return translated error message or null
    return errorKey ? t(errorKey) : null
  }

  // Validate without translation (returns i18n key only)
  const validateKey = (field: Field, value: string): string | null => {
    switch (field) {
      case 'firstName':
      case 'name':
        return validateFirstName(value)
      case 'lastName':
        return validateLastName(value)
      case 'email':
        return validateEmail(value)
      case 'password':
        return validatePassword(value)
      case 'company':
        return validateCompany(value)
      case 'position':
        return value.trim().length < 2 ? 'enterValidPosition' : null
      case 'code':
        return value.length !== 6 || !/^\d{6}$/.test(value)
          ? 'enterValidCode'
          : null
      default:
        return null
    }
  }

  return {
    validate, // Returns translated message
    validateKey, // Returns i18n key only
    validatePasswordStrength, // Returns { validLength, validCharacters } for real-time UI feedback
    validatePassword, // Returns password validation error message
  }
}
